import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tap-to-record voice input. Records with MediaRecorder, sends the audio to the
// Whisper transcription endpoint, and hands the text back via onTranscript.
export function MicButton({ onTranscript, disabled = false, className = "", title = "Speak", testid = "mic-btn" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const transcribe = async (blob) => {
    setState("transcribing");
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("audio", blob, "recording.webm");
      const resp = await fetch(`${API}/transcribe`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!resp.ok) {
        let detail;
        try { detail = (await resp.json()).detail; } catch (e) {}
        throw new Error(detail || `Transcription failed (${resp.status})`);
      }
      const data = await resp.json();
      const text = (data.text || "").trim();
      if (text) onTranscript?.(text);
      else toast.message("Didn't catch that — please try again.");
    } catch (e) {
      toast.error(e.message || "Could not transcribe audio.");
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stopTracks();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 0) transcribe(blob);
        else setState("idle");
      };
      rec.start();
      recorderRef.current = rec;
      setState("recording");
    } catch (e) {
      toast.error("Microphone access denied or unavailable.");
      setState("idle");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const onClick = () => {
    if (state === "idle") start();
    else if (state === "recording") stop();
  };

  const busy = state === "transcribing";
  const recording = state === "recording";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={testid}
      title={recording ? "Stop & transcribe" : busy ? "Transcribing…" : title}
      aria-label={recording ? "Stop recording" : "Start voice input"}
      className={`shrink-0 grid place-items-center border transition-colors h-11 w-11 ${
        recording
          ? "border-destructive text-destructive bg-destructive/10 animate-pulse"
          : "border-border text-muted-foreground hover:text-brand hover:border-brand"
      } disabled:opacity-50 ${className}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" />
        : recording ? <Square className="w-4 h-4 fill-current" />
        : <Mic className="w-4 h-4" />}
    </button>
  );
}
